import { useCallback, useEffect, useRef } from 'react';
import { createSyntheticEvent } from '@vfit/shared/hooks';
import { IUseOneNumberInput } from './oneNumberInput.models';

const NOT_NUMBER = /[^0-9]/g;

export const useOneNumberInput = ({
  focusedRef,
  onOtpBlur,
  onOtpChange,
  otpRef,
}: IUseOneNumberInput) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const validate = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>) => {
      if (inputRef?.current) {
        const [first, ...restValues] = e.target.value.replace(NOT_NUMBER, '');

        inputRef.current.value = first !== undefined ? first : '';

        if (otpRef.current) {
          const childs = inputRef.current.parentElement?.children;

          if (childs) {
            let v = '';

            for (let i = 0; i < childs.length; i += 1) {
              const child = childs[i].nodeName === 'INPUT' ? (childs[i] as HTMLInputElement) : null;
              v += child && child.value ? child.value : '';
            }

            // eslint-disable-next-line no-param-reassign
            otpRef.current.value = v;

            const syntheticEvent = createSyntheticEvent(otpRef.current, 'change');

            if (syntheticEvent) {
              onOtpChange?.(syntheticEvent);
            }
          }
        }

        if (e.target?.nextSibling && e.target?.nextSibling.nodeName === 'INPUT') {
          const nextOtp = e.target.nextSibling as HTMLInputElement;

          const nextValue = restValues.join('');

          if (nextValue) {
            nextOtp.value = `${nextValue}${nextOtp.value}`;
            nextOtp.focus();
          }
        }
      }
    },
    [onOtpChange, otpRef]
  );

  const handleBlur = useCallback(() => {
    // eslint-disable-next-line no-param-reassign
    focusedRef.current = false;

    const syntheticEvent = createSyntheticEvent(otpRef.current as HTMLInputElement, 'blur');

    if (syntheticEvent) {
      onOtpBlur?.(syntheticEvent);
    }
  }, [focusedRef, onOtpBlur, otpRef]);

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      e.target.value = ''; // Temporarily clear value
      e.target.value = inputValue; // re-assign after the cursor automatically moved to the right of the input field

      // eslint-disable-next-line no-param-reassign
      focusedRef.current = true;

      validate(e);
    },
    [focusedRef, validate]
  );

  const jumpToPrevious = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const el = e.target as HTMLInputElement;
    if (e.key === 'Backspace' && el.value === '') {
      if (el.previousSibling && el.previousSibling.nodeName === 'INPUT') {
        const previous = el.previousSibling as HTMLInputElement;
        previous.focus();
      }
    }
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const el = e.target as HTMLInputElement;

    if (e.key === 'Backspace') {
      if (el.value === '' && el.previousSibling && el.previousSibling.nodeName === 'INPUT') {
        const previous = el.previousSibling as HTMLInputElement;
        return previous.focus();
      }
      if (el.value != '') {
        el.value = '';
        return el.focus();
      }
    }

    if (e.ctrlKey || e.metaKey) {
      return;
    }

    if (Number.isNaN(Number(e.key))) e.preventDefault();
  }, []);

  // Handle default value
  useEffect(() => {
    if (
      inputRef.current &&
      inputRef.current === inputRef.current.parentElement?.children[0] &&
      inputRef.current.value !== undefined
    ) {
      inputRef.current.value = otpRef.current?.value || '';
      if (inputRef.current.value) {
        const syntheticEvent = createSyntheticEvent(inputRef.current, 'change');

        if (syntheticEvent) {
          validate(syntheticEvent);
        }
      }
    }
  }, []);

  return {
    handleBlur,
    handleFocus,
    inputRef,
    jumpToPrevious,
    handleKeyDown,
    validate,
  };
};
